import { CustomError } from '@common/util/custom-error';
import { WorkerTimeoutName } from '@common/workers/constants/worker-timeouts.constant';
import { Subject } from 'rxjs';

/**
 * Base class for a task worker.
 * A task worker is a web worker that performs a task and emits the result when the task completes.
 * If the task worker is no longer needed, it should be destroyed to free up resources.
 * If the task worker is instructed to run while it is already running, the previous task is terminated, and the new task is run.
 * @template R The type of the result emitted when the task completes.
 */
export class TaskWorkerBase<R extends { type: 'complete' }> {
  /** Emits the result when the task completes. */
  public complete$: Subject<R> = new Subject<R>();

  /** Web Worker for the task. */
  private worker: Worker;

  /**
   * Creates a new task worker.
   * @param name The name of the task worker. Used in logging.
   * @param createWorker A function that creates a new web worker for the task.
   */
  constructor(protected name: string, private createWorker: () => Worker) { }

  /**
   * Terminates the web worker and completes the complete$ observable.
   * The task worker can no longer be used after this method is called.
   */
  public destroy() {
    this.worker?.terminate();
    this.worker = null;
    this.complete$.complete();
  }

  /**
   * Runs the web worker with the provided message.
   * @param message The message to send to the web worker.
   * @param timeoutMS Optional timeout in milliseconds for the worker to complete.
   * @returns A promise that resolves with the result of the worker.
   */
  public run<T>(message: T, timeoutMS?: number): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      this.worker?.terminate();
      this.worker = this.createWorker();

      let timer: NodeJS.Timeout;

      if (typeof timeoutMS === 'number') {
        timer = setTimeout(() => {
          this.worker?.terminate();
          reject(new CustomError(WorkerTimeoutName, `${this.name} timed out after ${timeoutMS}ms`));
        }, timeoutMS);
      }

      this.worker.onmessage = ({ data }: { data: R }) => {
        if (data.type === 'complete') {
          clearTimeout(timer);
          this.complete$.next(data);
          resolve(data);
        }
      };

      this.worker.onerror = (error) => {
        clearTimeout(timer);
        reject(error);
        console.error(`[${this.name}] worker error`, error);
      };

      this.worker.postMessage(message);
    });
  }

  /** Terminates the web worker if it is running. */
  public stop() {
    this.worker?.terminate();
    this.worker = null;
  }
}
